<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { isAfter, parseISO, subDays } from 'date-fns';

const props = defineProps({
  comment: { type: Object, required: true },
});

const emit = defineEmits(['reply', 'privateReply']);

const PRIVATE_REPLY_WINDOW_DAYS = 7;

const { t } = useI18n();

const message = ref('');

// Meta rejects a second private reply to the same comment, and rejects any private
// reply to a comment older than 7 days. Both are predictable, so the button disables
// itself with a stated reason rather than letting the agent discover it via a raw
// Graph error. A comment first seen through a hide/remove webhook has no commentedAt
// -- unknown age is not a reason to block a legitimate action, so that case stays
// enabled. Mirrors Social::Facebook::CommentActions.private_reply_available?.
const privateReplyDisabledReason = computed(() => {
  if (props.comment.privateReplySentAt) {
    return t('COMMENTS.REPLY_BOX.PRIVATE_REPLY_REASON.ALREADY_SENT');
  }
  if (
    props.comment.commentedAt &&
    !isAfter(
      parseISO(props.comment.commentedAt),
      subDays(new Date(), PRIVATE_REPLY_WINDOW_DAYS)
    )
  ) {
    return t('COMMENTS.REPLY_BOX.PRIVATE_REPLY_REASON.WINDOW_EXPIRED');
  }
  return '';
});

const isPrivateReplyDisabled = computed(() =>
  Boolean(privateReplyDisabledReason.value)
);

const handleReply = () => {
  if (!message.value.trim()) return;
  emit('reply', message.value);
};

const handlePrivateReply = () => {
  if (isPrivateReplyDisabled.value || !message.value.trim()) return;
  emit('privateReply', message.value);
};

// The box is cleared by the parent once the Graph call has actually succeeded,
// never on emit: a failed reply used to leave the agent with an empty textarea,
// no error, and a comment they believed they had answered. Same success-callback
// shape CompaniesIndex.vue uses for its create dialog.
const onSuccess = () => {
  message.value = '';
};

defineExpose({ onSuccess });
</script>

<template>
  <div class="flex flex-col w-full gap-2 p-4 border-t border-n-weak">
    <textarea
      v-model="message"
      data-testid="reply-textarea"
      rows="3"
      :placeholder="t('COMMENTS.REPLY_BOX.PLACEHOLDER')"
      class="w-full p-2 text-sm border rounded-lg resize-none border-n-weak bg-n-solid-2 text-n-slate-12"
    />
    <div class="flex items-center gap-3">
      <button
        type="button"
        data-testid="reply-button"
        class="h-8 px-3 text-sm font-medium text-white rounded-lg bg-n-blue-9 disabled:opacity-50"
        @click="handleReply"
      >
        {{ t('COMMENTS.REPLY_BOX.REPLY_BUTTON') }}
      </button>
      <button
        type="button"
        data-testid="private-reply-button"
        :disabled="isPrivateReplyDisabled"
        class="h-8 px-3 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 disabled:opacity-50"
        @click="handlePrivateReply"
      >
        {{ t('COMMENTS.REPLY_BOX.PRIVATE_REPLY_BUTTON') }}
      </button>
      <span
        v-if="privateReplyDisabledReason"
        data-testid="private-reply-reason"
        class="text-xs text-n-slate-10"
      >
        {{ privateReplyDisabledReason }}
      </span>
    </div>
  </div>
</template>
